import { Lightning as lng, Log, Registry, Router, Utils } from '@lightningjs/sdk';
import { DetailDescription } from './DetailDescription';
import { IAssetV5, IProperty } from '../../api/models/AndromeTV.types';
import { fullUrlWithRecipe, Recipes } from '../../Business/utility/ImageRecipeUtil';
import { Backend } from '../../api/Backend';
import { Theme } from '../../config/theme';
import { IDetailDescription } from '../../Business/detail/detail.types';
import { VodDetailDescriptionDataSource } from '../../Business/detail/vod/VodDetailDescriptionDataSource';
import { Button } from '../../components/shared/Button';
import { AutoplayTranslationKeys } from '../../api/models/TranslationKeys';
import { GlobalSettingsKeys } from '../../api/models/GlobalSettingsKeys';
import { addProperty } from '../../api/analytics/AnalyticsUtil';
import { AutoplayActions, AutoplaySubcategories, MainCategories, PropertyKeys } from '../../api/analytics/ClientEvents';
import { StreamInfo } from '../../Business/player/streamInfo/StreamInfo';
import { AssetType } from '../../api/models/Shared.types';

const _constants = {
  padding: 50,
};

export class AutoplayScreen extends lng.Component {
  private timerInterval: number | undefined;
  private backend: Backend | undefined;
  private asset: IAssetV5 | undefined;
  private previousAsset!: StreamInfo;

  static override _template() {
    return {
      x: 0,
      y: 0,
      w: (w: number) => w,
      h: (h: number) => h,
      Background: {
        w: (w: number) => w,
        h: (h: number) => h,
        // rect: true,
        src: Utils.asset('images/background.png'),
      },
      Gradient: {
        y: (h: number) => h / 2,
        h: (h: number) => h / 2,
        w: (w: number) => w,
        TopPart: {
          h: (h: number) => h * 0.4,
          w: (w: number) => w,
          rect: true,
          colorTop: Theme.Colors.Transparent,
          colorBottom: 0xb0000000, // 69% black
        },
        BottomPart: {
          y: (h: number) => h * 0.4,
          h: (h: number) => h * 0.6,
          w: (w: number) => w,
          rect: true,
          colorTop: 0xb0000000, // 69% black
          colorBottom: Theme.Colors.Black,
        },
      },
      Description: {
        mountY: 1,
        y: (h: number) => h - _constants.padding,
        x: _constants.padding,
        type: DetailDescription,
        autoplayScreen: true,
      },
      ContinueButton: {
        mount: 1,
        x: (w: number) => w - _constants.padding,
        y: (h: number) => h - _constants.padding,
        h: 73,
        type: Button,
        label: 'Test',
        buttonMode: 'autoplayButton',
        signals: {
          onActivate: '_handleConfirm',
        },
      },
    };
  }

  private _detailDescription = this.tag('Description') as DetailDescription;
  private _button = this.tag('ContinueButton') as Button;

  override set params(data: { backend: Backend; previousAsset: StreamInfo; data: IAssetV5; enableCountDown: boolean }) {
    this.backend = data.backend;
    this.asset = data.data;
    this.previousAsset = data.previousAsset;
    Log.info('[Autoplay] screen data:', data);
    const assetData = data.data;
    const enableCountDown = data.enableCountDown;
    // set background image
    this.setBackground(data.backend, assetData);
    // set description data
    this.updateDescription(data.backend, assetData);
    // set button countdown enabled yes or no
    this.updateButton(data.backend, assetData, enableCountDown);
  }

  override _active() {
    const properties: IProperty[] = [];
    addProperty(PropertyKeys.assetId, this.previousAsset.assetId, [], properties);
    addProperty(PropertyKeys.assetType, this.previousAsset.type, [], properties);
    if (this.asset) addProperty(PropertyKeys.nextAssetId, this.asset.assetId, [], properties);
    addProperty(PropertyKeys.nextAssetType, AssetType.VOD, [], properties); // Manually set VOD, check when autoplay for recording
    this.backend?.logService.createClientEventEntry(
      { type: MainCategories.AUTOPLAY, subType: AutoplaySubcategories.PAGE_VIEW },
      properties
    );
  }

  override _getFocused() {
    return this._button;
  }

  override _handleBack() {
    this.clearTimer();
    // log close
    this.logAction(AutoplayActions.CLOSE);
    this.backend?.playerController.fallbackToLinearStream();
    this.fireAncestors('$closeModal');
    // return false;
  }

  private setBackground(backend: Backend, asset: IAssetV5) {
    if (asset.image.detail) {
      this.tag('Background').src = fullUrlWithRecipe(backend, asset.image.detail, Recipes.AutoplayFullscreen);
    } else if (asset.image.poster) {
      this.tag('Background').src = fullUrlWithRecipe(backend, asset.image.poster, Recipes.AutoplayFullscreen);
    } else {
      // default background color
      this.tag('Background').src = Utils.asset('images/background.png');
    }
  }

  private updateDescription(backend: Backend, asset: IAssetV5) {
    this._detailDescription.content = {
      synopsis: asset.text.longDescription || asset.text.shortDescription,
      title: asset.text.title || asset.text.originalTitle,
      secondaryMetaInfo: VodDetailDescriptionDataSource.getSecondaryMetaInfo(backend, asset),
    };
  }

  private updateButton(backend: Backend, asset: IAssetV5, enableCountDown: boolean) {
    if (enableCountDown) {
      const labelTemplate = backend.translations.forKey(AutoplayTranslationKeys.autoplay_start_in_seconds);
      let time = backend.globalSettings.numberValue(GlobalSettingsKeys.autoplayStartNextAssetTime) || 9;
      this._button.label = labelTemplate.replace('$1', time.toString());
      this.timerInterval = Registry.setInterval(() => {
        time = time - 1;
        this._button.label = labelTemplate.replace('$1', time.toString());
        if (time <= 0) {
          // at the end
          Log.info('[Autoplay] button timer end reached');
          // stop interval
          this.clearTimer();
          // log auto start
          this.logAction(AutoplayActions.NO_INTERACTION);
          // start stream
          this.startStream();
        }
      }, 1000);
    } else {
      this._button.label = backend.translations.forKey(AutoplayTranslationKeys.autoplay_start_manually);
    }
  }

  private clearTimer() {
    if (this.timerInterval) {
      Registry.clearInterval(this.timerInterval);
      this.timerInterval = undefined;
    }
  }

  private _handleConfirm() {
    Log.info('[Autoplay] confirm presed');
    this.clearTimer();
    this.logAction(AutoplayActions.MANUAL);
    this.startStream(true);
  }

  private startStream(resetAutoplay = false) {
    Log.info('[Autoplay] start stream');
    this.fireAncestors('$closeModal');
    const streamInfo = this.backend?.playerController.getAutoplayStreamInfo;
    Router.navigate('play', {
      streamInfo,
      resetAutoplay,
    });
  }

  private logAction(action: AutoplayActions) {
    const properties: IProperty[] = [];
    addProperty(PropertyKeys.action, action, [], properties);
    addProperty(PropertyKeys.assetId, this.previousAsset.assetId, [], properties);
    addProperty(PropertyKeys.assetType, this.previousAsset.type, [], properties);
    if (this.asset) addProperty(PropertyKeys.nextAssetId, this.asset.assetId, [], properties);
    addProperty(PropertyKeys.nextAssetType, AssetType.VOD, [], properties); // Manually set VOD, check when autoplay for recording
    this.backend?.logService.createClientEventEntry(
      { type: MainCategories.AUTOPLAY, subType: AutoplaySubcategories.ACTION },
      properties
    );
  }
}
